import * as React from 'react';
import { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Routes, useJourneyNavigation } from '../../../routes';
import { OrgPageWidget } from '../../../widgets/page/OrgPageWidget';
import { PageHeadingWidget } from '../../../widgets/page/PageHeadingWidget';
import { useOrgStore } from '../../../hooks/useOrgStore';
import { ErrorCodes, TicketStatus } from '@journeyapps-platform/types-intercom';
import { TableRow } from '../../../widgets/table/TableWidget';
import { Route, Routes as RoutesSwitch } from 'react-router-dom';
import { SupportPageDialog } from './SupportPageDialog';
import { LayerWidget } from '../../../layers/LayerWidget';
import { IntercomTicketModel } from '@journeyapps-platform/lib-journey-core';
import { useSystem } from '../../../hooks/useSystem';
import { SupportTicketTableWidget } from './SupportTicketTableWidget';
import { isJourneyError } from '@journeyapps-labs/micro-errors';
import { EmptyItemsWidget } from '../../../widgets/EmptyItemsWidget';

export interface SupportRow extends TableRow {
  model: IntercomTicketModel;
}

export const SupportPage: React.FC = observer(() => {
  const system = useSystem();
  const orgStore = useOrgStore();
  const { relativeRoute, navigate } = useJourneyNavigation(Routes.SUPPORT);
  const org = orgStore.current_org!;
  const [supportEnabled, setSupportEnabled] = useState<boolean | null>(() => {
    if (org?.tickets?.items?.length > 0) {
      return true;
    }
    return null;
  });

  useEffect(() => {
    org
      ?.loadTickets({})
      .then(() => {
        setSupportEnabled(true);
      })
      .catch((ex) => {
        if (isJourneyError(ex) && ex.errorData.code === ErrorCodes.ORG_NOT_CONFIGURED) {
          setSupportEnabled(false);
        }
      });
  }, [org]);

  let tickets = org?.tickets?.items || [];

  return (
    <OrgPageWidget activeItemKey={Routes.SUPPORT} loading={supportEnabled == null}>
      <PageHeadingWidget title={'Organization Support'} />
      {(supportEnabled ?? true) ? (
        <SupportTicketTableWidget tickets={tickets} />
      ) : (
        <EmptyItemsWidget label="Integrated support has not yet been enabled for this organization." />
      )}
      <RoutesSwitch>
        <Route
          path={relativeRoute(Routes.SUPPORT_CREATE)}
          element={
            <LayerWidget<Routes.SUPPORT_CREATE>
              route={Routes.SUPPORT_CREATE}
              options={{ withBackdrop: true, userCanExit: false }}
              renderLayer={({ handler }) => {
                return (
                  <SupportPageDialog
                    handler={handler}
                    success={() => {
                      navigate(Routes.SUPPORT, {
                        org_id: org.org_id
                      });
                      system.dialogStore.infoDialog({
                        title: 'Request submitted!',
                        message: `Your ticket has been successfully submitted, and our support team has been notified. It may take a moment for the ticket to appear here. A confirmation from JourneyApps Support has been sent to your email, and you’ll receive updates both here and via email.`
                      });
                    }}
                    cancel={() => {
                      navigate(Routes.SUPPORT, {
                        org_id: org.org_id
                      });
                    }}
                  />
                );
              }}
            />
          }
        ></Route>
      </RoutesSwitch>
    </OrgPageWidget>
  );
});
